import { TextField } from '@repo/design-system-kit';
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';
import { formatIban, handleFormatIban } from 'receivable-invoices-js/react/utils/format-input';

interface IbanFieldProps extends ComponentPropsWithoutRef<'div'> {
  value?: string;
  onUpdate?: (value: string) => void;
  error?: string;
}

export function IbanField({ value = '', onUpdate, error, className }: IbanFieldProps): ReactNode {
  const { formatMessage } = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);

  const formattedDefaultValue = formatIban(value, false) ?? '';

  useEffect(() => {
    if (inputRef.current) {
      const formattedValue = formatIban(value, false) ?? '';
      if (inputRef.current.value !== formattedValue) {
        inputRef.current.value = formattedValue;
      }
    }
  }, [value]);

  const inputRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        const input = node.querySelector('input');
        if (input && inputRef.current !== input) {
          (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = input;

          const formattedValue = formatIban(value, false) ?? '';
          input.value = formattedValue;
        }
      }
    },
    [value]
  );

  const handleInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target;

      handleFormatIban(input, false);

      const rawValue = input.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

      if (onUpdate) {
        onUpdate(rawValue);
      }
    },
    [onUpdate]
  );

  return (
    <div ref={inputRefCallback}>
      <TextField
        autoComplete="off"
        className={className}
        data-testid="iban-field-input"
        defaultValue={formattedDefaultValue}
        errorMessage={error}
        isInvalid={Boolean(error)}
        label={formatMessage({ id: 'labels.IBAN' })}
        onInput={handleInput}
        placeholder={formatMessage({ id: 'placeholders.iban' })}
      />
    </div>
  );
}
